import { useEffect, useState } from "react";
import { api } from "./lib/api";

// A CLIENT'S CREDIT, ON THE CLIENT (2026-10-26). Caleb: the place to add credit "needs to be
// associated with the client on their client page" -- not a dropdown of every campaign on the Money
// tab. Credit reaches a campaign two ways: the campaign buys it with a card on its own Credit tab,
// or Orator staff add it here, on Client accounts → Manage → Credit: free credit, a check or wire
// received, or a correction.
//
// FREE CREDIT IS NOT INCOME. It is written as an adjustment named "Free credit", so "Paid in" on the
// Money tab does not move when Orator gives credit away (backend/billing/credit.py staff_credit).

export const money = (c: number | null | undefined) =>
  c == null ? "—" : `${c < 0 ? "-" : ""}$${(Math.abs(c) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The Credit cell on a campaign's row: dim when there is none, so the ones that hold some stand out. */
export function CreditCell({ cents }: { cents?: number }) {
  const has = (cents ?? 0) !== 0;
  return <td className={has ? undefined : "muted"} style={{ fontVariantNumeric: "tabular-nums", fontWeight: has ? 700 : undefined, whiteSpace: "nowrap" }}>
    {cents == null ? "—" : money(cents)}
  </td>;
}

type Row = { amount_cents: number; kind: string; description: string | null; at: string | null; by: string | null };
type Credit = { name: string; balance_cents: number; auto_topup: boolean; card_last4: string | null; recent: Row[] };

export function CreditTab({ campaignId }: { campaignId: string }) {
  const [c, setC] = useState<Credit | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [kind, setKind] = useState<"free" | "check" | "adjustment">("free");
  const [amt, setAmt] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function load() {
    try { setC(await api<Credit>(`/staff/campaign-credit?campaign_id=${encodeURIComponent(campaignId)}`)); setErr(null); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
  }
  useEffect(() => { setC(null); load(); }, [campaignId]);

  const dollars = parseFloat(amt.replace(/[$,\s]/g, ""));
  const ok = Number.isFinite(dollars) && dollars !== 0 && (kind === "adjustment" || dollars > 0) && !!note.trim();
  async function save() {
    setBusy(true); setMsg(null);
    try {
      await api("/billing/admin/credit", { method: "POST", body: JSON.stringify({
        campaign_id: campaignId, amount_cents: Math.round(dollars * 100), kind, note: note.trim() }) });
      setMsg({ ok: true, text: `Added ${money(Math.round(dollars * 100))}.` });
      setAmt(""); setNote(""); await load();
    } catch (e: any) { setMsg({ ok: false, text: String(e?.message ?? e) }); } finally { setBusy(false); }
  }

  // A balance read off a failed request is not $0.00: say it couldn't load.
  if (err && !c) return <div className="muted" style={{ color: "var(--warn,#eab308)" }}>Couldn&rsquo;t load the credit — this is not a balance of zero. {err}</div>;
  if (!c) return <div className="muted">Loading…</div>;
  return <>
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
      <span className="muted" style={{ fontSize: 13 }}>Balance</span>
      <span style={{ fontSize: 24, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{money(c.balance_cents)}</span>
      <span className="muted" style={{ fontSize: 12.5 }}>
        · automatic top-up {c.auto_topup ? "on" : "off"} · {c.card_last4 ? `card ending ${c.card_last4}` : "no card on file"}
      </span>
    </div>
    <div>
      <div className="fld" style={{ marginBottom: 6 }}>Add credit</div>
      <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>What it is
          <select className="ctl" value={kind} onChange={(e) => setKind(e.target.value as "free" | "check" | "adjustment")}>
            <option value="free">Free credit</option>
            <option value="check">Payment received (check or wire)</option>
            <option value="adjustment">Correction</option>
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>Amount ($)
          <input className="ctl" style={{ width: 90 }} value={amt} onChange={(e) => setAmt(e.target.value)} inputMode="decimal" placeholder="25.00" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, flex: 1, minWidth: 160 }}>
          {kind === "check" ? "Check number or wire reference" : "Why"}
          <input className="ctl" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={kind === "free" ? "e.g. Testing paid calls" : kind === "check" ? "#1042 or wire ref" : "e.g. Refund for a failed send"} />
        </label>
        <button className="btn accent" disabled={busy || !ok} onClick={save}>{busy ? "Adding…" : "Add"}</button>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
        {kind === "free" ? "Free credit spends like bought credit and isn't counted as money paid in."
          : kind === "check" ? "A check or wire counts as money paid in."
          : "A correction can be negative, e.g. -25.00. It isn't counted as money paid in."}
      </div>
      {msg && <div style={{ fontSize: 13, marginTop: 6, color: msg.ok ? "var(--accent, #1a8a4a)" : "var(--bad)", fontWeight: msg.ok ? undefined : 600 }}>{msg.text}</div>}
    </div>
    <div>
      <div className="fld" style={{ marginBottom: 6 }}>Recent</div>
      {c.recent.length === 0
        ? <div className="muted" style={{ fontSize: 13 }}>No credit added yet.</div>
        : <table><tbody>
            {c.recent.map((r, i) => (
              <tr key={i}>
                <td className="muted" style={{ whiteSpace: "nowrap" }}>{r.at ? new Date(r.at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}</td>
                <td>{r.description || r.kind}</td>
                <td className="muted">{r.by || ""}</td>
                <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{r.amount_cents > 0 ? "+" : ""}{money(r.amount_cents)}</td>
              </tr>
            ))}
          </tbody></table>}
      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>The full history stays on the campaign&rsquo;s own Credit tab, with the CSV download.</div>
    </div>
  </>;
}
